import React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import FormFieldLabel from "../../../../components/common/FormFieldLabel";

import "../../../../css/cards.css";
import "../../../../css/forms.css";
import "../../../../css/buttons.css";

export interface Agreement {
  agreementCode: string;
  title: string;
  text: string;
  accepted: boolean;
}

interface AgreementCardProps {
  compact?: boolean;
  agreements: Agreement[];
  onChange: (value: Agreement[]) => void;
}

export const createDefaultAgreements = (): Agreement[] => [
  {
    agreementCode: "RESPECT",
    title: "Respect",
    text: "Respect the Youth Project Team and other young people.",
    accepted: false,
  },
  {
    agreementCode: "INDIVIDUAL",
    title: "Individual",
    text: "Challenge the view rather than the individual.",
    accepted: false,
  },
  {
    agreementCode: "BEHAVIOUR",
    title: "Behaviour",
    text: "Behave safely and respectfully during sessions and activities.",
    accepted: false,
  },
  {
    agreementCode: "WARNINGS",
    title: "Warnings",
    text: "Warnings may be given if behaviour expectations are not followed.",
    accepted: false,
  },
  {
    agreementCode: "WITHDRAWAL",
    title: "Withdrawal",
    text: "Continued poor behaviour may result in withdrawal from the programme.",
    accepted: false,
  },
  {
    agreementCode: "CONTACT_WITH_CONCERNS",
    title: "Contact With Concerns",
    text: "Concerns can be raised with staff or the Youth Project team.",
    accepted: false,
  },
];

const agreementHelpText: Record<string, string> = {
  RESPECT:
    "I agree that in order to attend any Youth Project Session I must respect the Youth Project Team and other young people.\n\nThis includes respecting views, beliefs, religion, lifestyle choices, sexual orientation, culture, ethnicity and appearance of others.",
  INDIVIDUAL:
    "During debate, workshops or conversations I will challenge the view rather than the individual.",
  BEHAVIOUR:
    "We understand that children and young people can be very passionate and competitive during sporting activities, however derogatory terms, swearing, insults and violence will not be tolerated.",
  WARNINGS:
    "If behaviour expectations are not followed, warnings may be given which may result in being asked to leave or miss sessions.",
  WITHDRAWAL:
    "Continued poor behaviour after warnings may result in withdrawal from the programme.",
  CONTACT_WITH_CONCERNS:
    "If negatively impacted by any behaviour listed, concerns can be raised with a member of staff at the session or by emailing the Youth Project team.",
};

export default function AgreementCard({
  agreements,
  compact = false,
  onChange,
}: AgreementCardProps) {
  const updateAgreement = (agreementCode: string, accepted: boolean) => {
    onChange(
      agreements.map((agreement) =>
        agreement.agreementCode === agreementCode
          ? { ...agreement, accepted }
          : agreement,
      ),
    );
  };

  if (compact) {
    return (
      <div className="card agreement-card agreement-card--compact">
        <div className="card-header"><div className="card-title">Agreements</div></div>
        <div className="family-agreement-list">
          {agreements.map((agreement) => (
            <div className="family-agreement-row" key={agreement.agreementCode}>
              <div>
                <FormFieldLabel component="span" label={agreement.title} helpText={agreementHelpText[agreement.agreementCode]} />
                <p className="field-hint">{agreement.text}</p>
              </div>
              <label className="family-agreement-choice">
                <input type="checkbox" checked={agreement.accepted}
                  aria-label={`I agree: ${agreement.title}`}
                  onChange={(e) => updateAgreement(agreement.agreementCode, e.target.checked)} />
                I agree
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card agreement-card">
      <div className="card-header">
        <div className="card-title">Agreements</div>
        <div className="field-hint">
          Review each agreement with the young person and confirm acceptance.
        </div>
      </div>

      {agreements.map((agreement) => (
        <Accordion
          key={agreement.agreementCode}
          className="agreement-card-accordion"
          disableGutters
          expanded
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" component="div">
              <FormFieldLabel
                component="span"
                className="agreement-summary-label"
                label={agreement.title}
                helpText={agreementHelpText[agreement.agreementCode]}
              />
            </Typography>
          </AccordionSummary>
          <AccordionDetails className="agreement-card-details">
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {agreement.text}
            </Typography>
            <div className="toggle-container">
              <label className="toggle-wrapper">
                <input
                  type="checkbox"
                  className="toggle-input"
                  checked={agreement.accepted}
                  onChange={(e) =>
                    updateAgreement(
                      agreement.agreementCode,
                      e.target.checked,
                    )
                  }
                />
                <span className="toggle-track" />
                <span className="toggle-thumb" />
              </label>
              <span className="toggle-label-text">I agree</span>
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
}
